import { useEffect, useState, useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { createColumnHelper } from '@tanstack/react-table'
// Import the new icons here
import {
  ReloadIcon,
  MagnifyingGlassIcon,
  Cross2Icon,
  ChatBubbleIcon,
  PlusIcon,
  IdCardIcon,
} from '@radix-ui/react-icons'

import useTitle from '../../hooks/useTitle'
import { Button } from '../../components/ui/button'
import axiosInstance from '../../api/axiosInstance'
import { AuthContext } from '../../contexts/auth-context'
import GenericTable from '../../components/generic-data-table/GenericTable'
import DataTableShimmer from '../../components/data-table/DataTableShimmer'
import { useNavigate } from 'react-router-dom'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'

import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group'
import { Label } from '../../components/ui/label'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import Tooltip from '../../components/tooltip/Tooltip' // Assuming your Tooltip component path is correct
import { Switch } from '@/components/ui/switch' // Assuming shadcn/ui switch
import { updateCustomerStatus } from '../../services'
import PlanUpgradeDialog from '@/components/ui/plan-upgrade-dialog'

const columnHelper = createColumnHelper()

const INITIAL_STATE = {
  searchTerm: '',
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
}

function BusinessCustomers() {
  const { auth } = useContext(AuthContext)
  const plan = auth?.user?.subscription?.planTier || 'basic'
  const [showPlanUpgrade, setShowPlanUpgrade] = useState(false)
  const [planUpgrade, setPlanUpgrade] = useState({
    name: '',
    desc: '',
  })
  const businessId = auth.user._id
  useTitle('Manage Customers')
  const queryClient = useQueryClient()

  const [customers, setCustomers] = useState([])
  const [searchTerm, setSearchTerm] = useState(INITIAL_STATE.searchTerm)
  const [pagination, setPagination] = useState(INITIAL_STATE.pagination)
  const [searchInput, setSearchInput] = useState('') // Local search input state
  const navigate = useNavigate()

  const [pointsDialogOpen, setPointsDialogOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [pointsForm, setPointsForm] = useState({
    type: 'credit',
    points: '',
    note: '',
  })
  const [formErrors, setFormErrors] = useState({})

  // Mutation for managing points
  const managePointsMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axiosInstance.post('web-api/rewards/points', {
        userCardId: selectedCustomer._id,
        ...formData,
      })
      return response.data
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Points updated successfully')
      setPointsDialogOpen(false)
      refetch()
      resetPointsForm()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update points')
    },
  })

  const resetPointsForm = () => {
    setPointsForm({
      type: 'credit',
      points: '',
      note: '',
    })
    setFormErrors({})
  }

  const handlePointsClick = (customer) => {
    setSelectedCustomer(customer)
    setPointsDialogOpen(true)
  }

  const handlePointsFormChange = (e) => {
    const { name, value } = e.target
    setPointsForm((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when field changes
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    // Validate points
    if (!pointsForm.points) {
      errors.points = 'Points is required'
    } else if (!/^\d+$/.test(pointsForm.points)) {
      errors.points = 'Points must be a number'
    } else if (parseInt(pointsForm.points) <= 0) {
      errors.points = 'Points must be greater than 0'
    }

    // Validate note
    if (!pointsForm.note) {
      errors.note = 'Note is required'
    } else if (pointsForm.note.length < 6) {
      errors.note = 'Note must be at least 6 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handlePointsSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      managePointsMutation.mutate({
        type: pointsForm.type,
        points: parseInt(pointsForm.points),
        note: pointsForm.note,
      })
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchInput(value)

    // Reset search and refetch when input becomes empty
    if (value === '') {
      setSearchTerm('')
      setPagination((prev) => ({ ...prev, page: 1 }))
    }
  }

  // Fetch customers data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['businessCustomers', businessId, pagination.page, pagination.limit, searchTerm],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get('web-api/business-customers', {
          params: {
            businessId,
            page: pagination.page,
            limit: pagination.limit,
            search: searchTerm,
          },
        })
        return response.data
      } catch (error) {
        throw new Error(error?.response?.data?.message || 'Error fetching customers')
      }
    },
  })

  useEffect(() => {
    if (data?.data?.customers) {
      setCustomers(data.data.customers)
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        totalPages: data.data.pagination.pages, // Map 'pages' to 'totalPages'
      }))
    }
  }, [data])

  const handleSearch = () => {
    if (searchInput !== searchTerm) {
      // Only trigger search if input has changed
      setSearchTerm(searchInput)
      setPagination((prev) => ({ ...prev, page: 1 }))
    }
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setSearchTerm('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(newLimit, 10),
      page: 1,
    }))
  }

  const handleRefresh = () => {
    setSearchTerm(INITIAL_STATE.searchTerm)
    setSearchInput(INITIAL_STATE.searchTerm)
    setPagination(INITIAL_STATE.pagination)
    refetch()
  }

  const columns = [
    columnHelper.accessor('customerId', {
      header: 'Customer ID',
      cell: (info) => info.getValue() || 'N/A',
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => info.getValue() || 'N/A',
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue() || 'N/A',
    }),
    columnHelper.accessor('totalRewardPoints', {
      header: 'Total Points',
      cell: (info) => info.getValue() || '0',
    }),
    columnHelper.accessor('mobile', {
      header: 'Mobile',
      cell: (info) => info.getValue() || '0',
    }),

    columnHelper.accessor('isCustomerActive', {
      header: 'Status',
      cell: (info) => {
        const row = info.row.original

        const handleToggle = async (checked) => {
          try {
            await updateCustomerStatus(row._id, checked)
            queryClient.invalidateQueries([
              'businessCustomers',
              businessId,
              pagination.page,
              pagination.limit,
              searchTerm,
            ])
            toast.success(`Customer status updated to ${checked ? 'Active' : 'Inactive'}`)
          } catch (error) {
            console.error(error)
            // toast is already handled in service, so no need to duplicate here
          }
        }

        return (
          <Switch
            className={`
            data-[state=checked]:bg-theme-color-800
            data-[state=checked]:hover:bg-theme-color-800
            data-[state=unchecked]:bg-gray-300
            transition-colors
          `}
            checked={row.isCustomerActive}
            onCheckedChange={handleToggle}
          />
        )
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          {/* Points Button */}
          <Tooltip text="Manage Points">
            <Button
              onClick={() => {
                if (plan?.toLowerCase() === 'basic') {
                  setPlanUpgrade({
                    name: 'Points Management',
                    desc: 'Credit/Debit reward points to boost loyalty and interaction with customers.',
                  })
                  setShowPlanUpgrade(true)
                  return
                }
                handlePointsClick(info.row.original)
              }}
              size="sm"
              className="bg-theme-color-800 hover:bg-theme-color-800 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" /> Points
            </Button>
          </Tooltip>

          {/* Card Button */}
          <Tooltip text="View Customer Card">
            <Button
              onClick={() => navigate(`card/${info.row.original._id}`)}
              size="sm"
              className="bg-theme-color-800 hover:bg-theme-color-800 flex items-center gap-1"
            >
              <IdCardIcon className="h-4 w-4" /> Card
            </Button>
          </Tooltip>

          {/* Message Button */}
          <Tooltip text="Message Customer">
            <Button
              onClick={() => {
                if (plan?.toLowerCase() === 'basic') {
                  setPlanUpgrade({
                    name: 'Personalized messaging',
                    desc: 'Send personalized messages to your customers and enhance engagement.',
                  })
                  setShowPlanUpgrade(true)
                  return
                }
                navigate(
                  `message/${info.row.original._id}/${encodeURIComponent(info.row.original.name)}/${encodeURIComponent(info.row.original.email)}`
                )
              }}
              size="sm"
              className="flex items-center gap-1 bg-theme-color-800 hover:bg-theme-color-800"
            >
              <ChatBubbleIcon className="h-4 w-4" /> Message
            </Button>
          </Tooltip>
        </div>
      ),
    }),
  ]

  const renderContent = () => {
    if (isLoading) {
      return <DataTableShimmer columns={5} />
    }

    if (error) {
      return (
        <div className="text-center py-12 px-6 border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-600">{error.message}</p>
          <Button onClick={handleRefresh} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      )
    }

    if (!customers.length) {
      return (
        <div className="text-center py-12 px-6 border border-dashed border-gray-300 rounded-lg bg-white">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="mt-3 text-lg font-semibold text-gray-800">No Customers Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm
              ? 'No customers match your search criteria. Try different keywords.'
              : "You don't have any customers yet."}
          </p>
        </div>
      )
    }

    return (
      <GenericTable
        columns={columns}
        data={customers}
        pagination={pagination}
        onPageChange={handlePageChange}
        isLoading={isLoading}
      />
    )
  }

  // here log the customer
  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-[calc(100vh-4rem)]">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Manage Customers</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-[250px]">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchInput}
                onChange={handleInputChange}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-theme-color-600 focus:border-transparent"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                  aria-label="Clear search"
                >
                  <Cross2Icon className="h-4 w-4 cursor-pointer" />
                </button>
              )}
            </div>
            <Button
              onClick={handleSearch}
              size="default"
              className="bg-theme-color-800 cursor-pointer hover:bg-theme-color-800 text-white rounded-r-md rounded-l-none border-l-0 flex items-center px-3 h-[38px]"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </Button>
          </div>
          {/* <select
            value={pagination.limit}
            onChange={(e) => handleLimitChange(e.target.value)}
            className="block w-full sm:w-auto pl-3 pr-8 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select> */}
          <div className="relative inline-block mr-3">
            <select
              id="itemsPerPage"
              name="itemsPerPage"
              value={pagination.limit}
              onChange={(e) => handleLimitChange(e.target.value)}
              // disabled={loading}
              className="block w-full sm:w-auto pl-3 pr-12 py-2 text-sm border-gray-300
               focus:outline-none focus:ring-theme-color-800 focus:border-theme-color-800
               rounded-md shadow-sm transition duration-150 ease-in-out"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>

            {/* custom arrow icon */}
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-800">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      {renderContent()}

      <Dialog
        open={pointsDialogOpen}
        onOpenChange={() => {
          setPointsDialogOpen(false)
          resetPointsForm()
        }}
      >
        <DialogContent className="sm:max-w-[425px] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Manage Points</DialogTitle>
          </DialogHeader>

          <form onSubmit={handlePointsSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="type">Transaction Type</Label>
              <RadioGroup
                id="type"
                value={pointsForm.type}
                onValueChange={(value) => setPointsForm((prev) => ({ ...prev, type: value }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="credit" id="credit" />
                  <Label htmlFor="credit">Credit</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="debit" id="debit" />
                  <Label htmlFor="debit">Debit</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="points">Points</Label>
              <Input
                id="points"
                name="points"
                type="number"
                min="1"
                value={pointsForm.points}
                onChange={handlePointsFormChange}
                className={formErrors.points ? 'border-red-500' : ''}
              />
              {formErrors.points && <p className="text-sm text-red-500">{formErrors.points}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="note">Remark</Label>
              <Textarea
                id="note"
                name="note"
                value={pointsForm.note}
                onChange={handlePointsFormChange}
                className={`
    max-h-[200px] min-h-[50px] overflow-y-auto max-w-[380px]
    break-words  // Tailwind equivalent of word-break: break-word
    ${formErrors.note ? 'border-red-500' : ''}
    resize-none
  `}
                placeholder="Enter reason for this transaction (min 6 chars)"
              />
              {formErrors.note && <p className="text-sm text-red-500">{formErrors.note}</p>}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPointsDialogOpen(false)
                  resetPointsForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-theme-color-800 hover:bg-theme-color-800"
                disabled={managePointsMutation.isPending}
              >
                {managePointsMutation.isPending ? (
                  <ReloadIcon className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  'Submit'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PlanUpgradeDialog
        showPlanUpgrade={showPlanUpgrade}
        setShowPlanUpgrade={setShowPlanUpgrade}
        name={planUpgrade.name}
        desc={planUpgrade.desc}
      />
    </div>
  )
}

export default BusinessCustomers
